import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { BRANDS, INSIGHTS_MENU } from '../../constants';
import { SideNav } from './components/SideNav/SideNav';
import GRSAIEnableBanner from './components/GRSAIEnableBanner/GRSAIEnableBanner.jsx';
import GRSAIEnableModal from './components/GRSAIEnableModal/GRSAIEnableModal.jsx';
import GRSAIDisableModal from './components/GRSAIDisableModal/GRSAIDisableModal';
import {
  getAssignmentPerformanceWidgets,
  getCoursePerformanceWidgets,
  shouldShowPerformance,
} from './helpers';
import { AssignmentPerformanceSelect } from './components/InsightsSelect/AssignmentPerformanceSelect';
import { CoursePerformanceSelect } from './components/CoursePerformanceSelect/CoursePerformanceSelect';
import useLocalStorage from './hooks/useLocalStorage';
import QuantQualInsights from '../QuantQualInsights';
import { GenerateAiSummariesButton } from '../QuantQualInsights/components/GenerateAiSummariesButton/GenerateAiSummariesButton.jsx';
import MLIcon from '../../../components/MLIcon/MLIcon';
import { getConfigObject } from '../../../util/config';
import { getBrand } from '../../utils';
import s from './InsightsTabs.module.css';
import { useGrsAiSummariesPreference } from '../../hooks/useGrsAiSummariesPreference.js';
import { useSaveCoursePreference } from '../../hooks/useSaveCoursePreference.js';
import { useAiFeatureAccess } from '../../hooks/useAiFeatureAccess.js';

const { ASSIGNMENT_PERFORMANCE, COURSE_PERFORMANCE, GRS } = INSIGHTS_MENU;

const APWidgets = () => {
  const { courseAbstract } = useSelector((state) => state.reports);
  const { assignments } = useSelector((state) => state.insights.dashboard);

  const filteredAssignments = useMemo(
    () => assignments?.filter((a) => shouldShowPerformance(a.tool)),
    [assignments]
  );

  const [
    { assignment_id: storedAssignmentId, current_course_id: storedCourseId },
    setAPMeta,
  ] = useLocalStorage('assignment_performence_meta', {
    current_course_id: '',
    assignment_id: '',
  });

  useEffect(() => {
    const courseId = courseAbstract?.id;
    if (courseId !== storedCourseId) {
      // Resets the selected assignment when the course changes
      setAPMeta({
        assignment_id: filteredAssignments?.[0]?.value,
        current_course_id: courseId,
      });
    }
  }, [filteredAssignments, courseAbstract?.id, setAPMeta, storedCourseId]);

  useEffect(() => {
    const isMatchingId =
      storedAssignmentId &&
      filteredAssignments?.some((a) => a.value === storedAssignmentId);

    if (!isMatchingId && filteredAssignments?.length) {
      setAPMeta({
        current_course_id: courseAbstract?.id,
        assignment_id: filteredAssignments[0]?.value,
      });
    }
  }, [filteredAssignments, courseAbstract?.id, setAPMeta, storedAssignmentId]);

  const widgets =
    filteredAssignments && filteredAssignments.length > 0 && storedAssignmentId
      ? getAssignmentPerformanceWidgets(storedAssignmentId)
      : [];

  return (
    <main>
      <header className={s.header}>
        <h1 className={s.title}>Assignment Performance</h1>
        <AssignmentPerformanceSelect
          onChange={(assignment) => {
            setAPMeta({
              current_course_id: courseAbstract?.id,
              assignment_id: assignment?.value,
            });
          }}
          assignments={filteredAssignments || []}
          selectedAssignmentId={storedAssignmentId}
        />
      </header>
      <QuantQualInsights widgetsV2={widgets} courseAbstract={courseAbstract} />
    </main>
  );
};

const CPWidgets = () => {
  const { courseAbstract } = useSelector((state) => state.reports);
  const {
    course: { data: courseData },
  } = useSelector((state) => state.courses);
  const [range, setDateRange] = useLocalStorage('date_range', {
    minRangeDays: 7,
    maxRangeDays: 0,
    selected_course_id: '',
    isCustom: false,
  });

  useEffect(() => {
    if (courseAbstract?.id !== range.selected_course_id) {
      setDateRange({
        minRangeDays: 7,
        maxRangeDays: 0,
        selected_course_id: courseAbstract?.id,
      });
    }
  }, [courseAbstract?.id, setDateRange, range.selected_course_id]);

  const hasExternalFramework = courseData.lo_framework_id;

  const widgets =
    getCoursePerformanceWidgets({
      maxRangeDays: range.maxRangeDays,
      minRangeDays: range.minRangeDays,
      hasExternalFramework,
    }) || [];

  return (
    <main>
      <header className={s.header}>
        <h1 className={s.title}>Course Performance</h1>
        <CoursePerformanceSelect
          dateRange={range}
          onDateChange={(dateRange, isCustom) =>
            setDateRange({
              selected_course_id: courseAbstract?.id,
              isCustom,
              ...dateRange,
            })
          }
          isCustomRange={range.isCustom}
        />
      </header>
      <QuantQualInsights widgetsV2={widgets} courseAbstract={courseAbstract} />
    </main>
  );
};

const GRSWidgets = () => {
  const { courseAbstract } = useSelector((state) => state.reports);
  const courseId = courseAbstract?.id;
  const [dropdown, setDropdown] = useState(null);
  const [selectedSurveyAssignmentId, setSelectedSurveyAssignmentId] = useState(
    null
  );
  const [isAiSummariesEnabled, setAiSummariesEnabled] = useState(false);
  const [isEnableAiModalOpen, setEnableAiModalOpen] = useState(false);
  const [isDisableAiModalOpen, setDisableAiModalOpen] = useState(false);

  const { FF_GRS_SUMMARIES_OPT_IN } = getConfigObject();
  const isHigherEd = getBrand() === BRANDS.HIGHER_ED;
  const {
    aiSummariesPreferenceEnabled,
    isLoading: isAiSummariesStatusLoading,
  } = useGrsAiSummariesPreference(courseId);
  const { mutateAsync: saveCoursePreference } = useSaveCoursePreference();
  const { aiFeatureAccess } = useAiFeatureAccess(courseId);
  const isContentGenerationAllowed =
    aiFeatureAccess.content_generation !== false;
  const aiSummariesEnabled =
    FF_GRS_SUMMARIES_OPT_IN &&
    isHigherEd &&
    aiSummariesPreferenceEnabled &&
    isContentGenerationAllowed;

  const showAiSummariesUi =
    !isAiSummariesStatusLoading && isContentGenerationAllowed;

  useEffect(() => {
    if (aiSummariesEnabled !== undefined) {
      setAiSummariesEnabled(aiSummariesEnabled);
    }
  }, [aiSummariesEnabled]);

  const handleEnableAiSummaries = async () => {
    if (courseId) {
      try {
        await saveCoursePreference({
          courseId,
          preferenceName: 'grs_ai_summaries',
          enabled: true,
        });
        setAiSummariesEnabled(true);
        setEnableAiModalOpen(false);
      } catch (error) {
        console.error('Failed to enable summaries:', error);
        setEnableAiModalOpen(false);
        alert('There was an error enabling summaries. Please try again.');
      }
    }
  };

  const handleDisableAiSummaries = async () => {
    if (courseId) {
      try {
        await saveCoursePreference({
          courseId,
          preferenceName: 'grs_ai_summaries',
          enabled: false,
        });
        setAiSummariesEnabled(false);
        setDisableAiModalOpen(false);
      } catch (error) {
        console.error('Failed to disable summaries:', error);
        setDisableAiModalOpen(false);
        alert('There was an error disabling summaries. Please try again.');
      }
    }
  };

  return (
    <main>
      <header className={s.header}>
        <h1 className={s.title}>Goal-setting and Reflection Responses</h1>
        <div className={s.headerActions}>
          {dropdown}
          {FF_GRS_SUMMARIES_OPT_IN &&
            isHigherEd &&
            isAiSummariesEnabled &&
            showAiSummariesUi && (
              <button
                className={s.grsAiDisableButton}
                onClick={() => setDisableAiModalOpen(true)}
                data-test-id="Insights_grs_summaries_disable_btn"
              >
                <MLIcon type="sparkle" className={s.sparkleIcon} />
                Turn Off Summaries
              </button>
            )}
          {isAiSummariesEnabled && showAiSummariesUi && (
            <GenerateAiSummariesButton
              courseId={courseId}
              assignmentId={selectedSurveyAssignmentId}
            />
          )}
        </div>
      </header>
      {FF_GRS_SUMMARIES_OPT_IN &&
        isHigherEd &&
        !isAiSummariesEnabled &&
        showAiSummariesUi && (
          <GRSAIEnableBanner onShowModal={() => setEnableAiModalOpen(true)} />
        )}
      <GRSAIEnableModal
        open={isEnableAiModalOpen}
        enableAiSummaries={handleEnableAiSummaries}
        onCancel={() => setEnableAiModalOpen(false)}
      />
      <GRSAIDisableModal
        open={isDisableAiModalOpen}
        disableAiSummaries={handleDisableAiSummaries}
        onCancel={() => setDisableAiModalOpen(false)}
      />
      <QuantQualInsights
        courseAbstract={courseAbstract}
        getSRLDropdown={setDropdown}
        setSelectedSurveyAssignmentId={setSelectedSurveyAssignmentId}
      />
    </main>
  );
};

const InsightsTabs = () => {
  const [insightsTab, setInsightsTab] = useLocalStorage(
    'selected_insights_tab',
    ASSIGNMENT_PERFORMANCE
  );

  return (
    <div className={s.reportsLayout}>
      <SideNav onTabSelect={setInsightsTab} selectedTab={insightsTab} />
      <div className={s.reportsContent} data-test-id="insights_tab_container">
        {insightsTab === ASSIGNMENT_PERFORMANCE && <APWidgets />}
        {insightsTab === COURSE_PERFORMANCE && <CPWidgets />}
        {insightsTab === GRS && <GRSWidgets />}
      </div>
    </div>
  );
};

export default InsightsTabs;
